import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AVERTISSEMENT_DOC,
  LIBELLES_TYPE_DOCUMENT,
  auMoins,
  jourDeTunis,
  peutDeplacerCandidature,
  quandPurge,
  rappelOrigine,
  telephoneLisible,
  lienWhatsapp,
  whatsappLisible,
  type CandidatureDetail,
  type ContactDetail,
  type DocumentResume,
  type LienPlateforme,
  type TypeContact,
} from '@rh/shared';
import { ErreurReseau, appel } from '../api';
import { useAuth } from '../auth/contexte-auth';
import { BadgeAdequation } from '../composants/Adequation';
import { AjouterTelephone, RetirerTelephone } from '../composants/AjouterTelephone';
import { ChangerStatut } from '../composants/ChangerStatut';
import { DeplacerCandidature } from '../composants/DeplacerCandidature';
import { Ecarter } from '../composants/Ecarter';
import { CvDeposeFiche } from '../composants/CvDeposeFiche';
import { DernierAppel, IconeTelephone } from '../composants/DernierAppel';
import { DeposerCv } from '../composants/DeposerCv';
import { EditerCharte } from '../composants/EditerCharte';
import { EditerRapport } from '../composants/EditerRapport';
import { Embaucher } from '../composants/Embaucher';
import { Chargement, Erreur } from '../composants/Etat';
import { FicheRecapitulative } from '../composants/FicheRecapitulative';
import { FlechesParcours } from '../composants/FlechesParcours';
import { FormulaireRecu } from '../composants/FormulaireRecu';
import { HistoriqueContacts, SaisiesLocales } from '../composants/HistoriqueContacts';
import { Justificatifs } from '../composants/Justificatifs';
import { NePlusContacter } from '../composants/NePlusContacter';
import { NoterPotentiel } from '../composants/NoterPotentiel';
import { Etoile } from '../composants/Etoile';
import { Note, Pastille } from '../composants/Pastille';
import { Prolonger } from '../composants/Prolonger';
import { LecteurDocument } from '../composants/LecteurDocument';
import { LienVersPlateforme } from '../composants/LienVersPlateforme';
import { MemoCandidat } from '../composants/MemoCandidat';
import { Signaux } from '../composants/Signaux';
import { Statut } from '../composants/Statut';
import { SupprimerPersonne } from '../composants/SupprimerPersonne';
import { classeNom, jourLisible, nomComplet } from '../format';
import { voisins } from '../parcours';
import { useFile } from '../horsligne/contexte-file';
import { memoire } from '../horsligne/memoire';
import { MESSAGE_OUVERTURE_HORS_LIGNE } from '../horsligne/textes';
import type { ResumeEnvoi } from '../horsligne/types';
import { telechargerDocument } from '../telechargement';

/** Mention sous « Voir sur Tunisie Travail », selon ce que le lien ouvre. */
const MENTIONS_LIEN_PLATEFORME: Record<LienPlateforme['type'], string> = {
  cvs_offre:
    'Liste des CV de l’offre dans votre espace recruteur : connectez-vous, téléchargez le CV du candidat, puis déposez-le ici.',
  offre_publique:
    'Page de l’offre : connectez-vous à votre espace recruteur pour retrouver le CV du candidat, puis déposez-le ici.',
};

/** Ancien Word (.doc) : ni texte ni aperçu, il ne s'ouvre jamais d'office dans le lecteur. */
const ancienWord = (document: DocumentResume) => /\.doc$/i.test(document.nomFichier);

const COULEUR_VERDICT: Record<string, string> = {
  conforme: 'verdict-conforme',
  partiel: 'verdict-partiel',
  manquant: 'verdict-manquant',
  indetermine: 'verdict-indetermine',
};

/** Écran 2 — fiche candidat (docs/UI.md). */
export function FicheCandidat() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  // L'ordre vient de la liste d'où l'on vient, filtres et tri compris : il est
  // relu à chaque changement de dossier (`src/parcours.ts`).
  const parcours = useMemo(() => voisins(id), [id]);
  const { utilisateur } = useAuth();
  const [fiche, setFiche] = useState<CandidatureDetail | null>(null);
  const [contacts, setContacts] = useState<ContactDetail[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [erreurAction, setErreurAction] = useState<string | null>(null);
  const [ouvert, setOuvert] = useState<DocumentResume | null>(null);
  const [horsLigne, setHorsLigne] = useState(false);
  const [essai, setEssai] = useState(0);
  // Chaque relecture de la fiche relit aussi le CV déposé (coordonnées lues par le worker).
  const [versionCv, setVersionCv] = useState(0);
  const { instantane, ecouter } = useFile();

  useEffect(() => {
    appel<CandidatureDetail>(`/candidatures/${id}`)
      .then((detail) => {
        memoire.retenirFiche({ ...detail, annonceIntitule: detail.annonceIntitule ?? null });
        setHorsLigne(false);
        setErreur(null);
        setFiche(detail);
        setOuvert(detail.documents.find((d) => d.type === 'cv' && !ancienWord(d)) ?? null);
      })
      // La fiche ne se lit pas hors ligne : seules les saisies du téléphone s'affichent.
      .catch((e: Error) => (e instanceof ErreurReseau ? setHorsLigne(true) : setErreur(e.message)));
    appel<ContactDetail[]>(`/candidatures/${id}/contacts`)
      .then(setContacts)
      .catch(() => setContacts([]));
  }, [id, essai]);

  useEffect(() => {
    if (!horsLigne) return;
    const surRetour = () => setEssai((n) => n + 1);
    window.addEventListener('online', surRetour);
    return () => window.removeEventListener('online', surRetour);
  }, [horsLigne]);

  // Saisies de ce téléphone pour cette candidature, par contact.
  const locales = useMemo(
    () => instantane.resumes.filter((r) => r.candidatureId === id),
    [instantane.resumes, id],
  );
  const envois = useMemo(() => new Map<string, ResumeEnvoi>(locales.map((r) => [r.contactId, r])), [locales]);

  // Une saisie de cette candidature vient d'être reçue : fiche et historique relus.
  const contactsConnus = useRef(new Set<string>());
  contactsConnus.current = new Set((contacts ?? []).map((c) => c.id));
  useEffect(
    () =>
      ecouter((evenement) => {
        if (evenement.type !== 'envoye') return;
        if (evenement.contact?.candidatureId === id || contactsConnus.current.has(evenement.contactId)) {
          setEssai((n) => n + 1);
        }
      }),
    [ecouter, id],
  );

  if (erreur) return <Erreur message={erreur} />;
  if (!fiche && horsLigne) {
    return (
      <article className="fiche">
        <p className="signal">Fiche indisponible hors ligne.</p>
        <div className="actions-fiche">
          <button type="button" className="bouton-second" onClick={() => setEssai((n) => n + 1)}>
            Réessayer
          </button>
        </div>
        <section className="bloc">
          <h3>Vos saisies pour ce candidat, gardées sur ce téléphone :</h3>
          <SaisiesLocales resumes={locales} />
        </section>
      </article>
    );
  }
  if (!fiche) return <Chargement quoi="de la fiche" />;

  const rh = utilisateur ? auMoins(utilisateur.role, 'rh') : false;
  const peutDeplacer = utilisateur
    ? peutDeplacerCandidature(utilisateur.role, utilisateur.deplacerCandidature)
    : false;
  const superAdmin = utilisateur?.role === 'super_admin';
  // Une fiche anonymisée ne se travaille plus : l'API refuse contacts, chartes et embauche.
  const anonymisee = fiche.anonymiseeLe !== null;
  // Écarté : on n'appelle plus (demande du 2026-09-19). « Noter un appel » reste,
  // pour consigner l'appel qui annonce la décision.
  const ecarte = fiche.statut === 'ecarte';

  /**
   * Relit la fiche après un document produit ou reçu : il rejoint la liste,
   * s'ouvre dans le lecteur, et statut et alertes se mettent à jour.
   */
  function recharger(documentOuvert?: string): void {
    void appel<CandidatureDetail>(`/candidatures/${id}`).then((detail) => {
      setFiche(detail);
      setVersionCv((n) => n + 1);
      const document = documentOuvert ? detail.documents.find((d) => d.id === documentOuvert) : undefined;
      if (documentOuvert && !(document && ancienWord(document))) setOuvert(document ?? null);
    });
  }

  /** Ouvre (ou rouvre, s'il est en cours) un contact et passe au panneau. */
  async function ouvrirContact(type: TypeContact): Promise<void> {
    setErreurAction(null);
    try {
      const contact = await appel<ContactDetail>(`/candidatures/${id}/contacts`, {
        method: 'POST',
        body: JSON.stringify({ type }),
      });
      // Dernière lecture gardée en mémoire : le panneau se réaffiche si le réseau coupe ensuite.
      memoire.retenirContact(contact);
      if (fiche) memoire.retenirFiche({ ...fiche, annonceIntitule: fiche.annonceIntitule ?? null });
      navigate(`/contacts/${contact.id}`);
    } catch (e) {
      setErreurAction(
        e instanceof ErreurReseau
          ? MESSAGE_OUVERTURE_HORS_LIGNE
          : e instanceof Error
            ? e.message
            : 'Ouverture du contact impossible',
      );
    }
  }

  const aCharte = fiche.documents.some((d) => d.type === 'charte_vierge');
  const aRapport = fiche.documents.some((d) => d.type === 'rapport');
  // Un CV, pas n'importe quel document : une charte éditée ne dit rien du CV.
  const aUnCv = fiche.documents.some((d) => d.type === 'cv');
  const tunisieTravail = fiche.source === 'tunisietravail' || fiche.lienPlateforme !== null;

  return (
    <article className="fiche">
      <header className="bandeau">
        <Pastille valeur={fiche.potentiel} libelle etiquette={fiche.etiquette} />
        <Etoile potentiel={fiche.potentiel} />
        <div>
          <div className="ligne-nom">
            <h2 className={classeNom(fiche.statut)}>{nomComplet(fiche)}</h2>
            {/* Petit mot collé à droite du nom, modifiable sur place (2026-09-19). */}
            <MemoCandidat
              candidatureId={fiche.id}
              memo={fiche.memo}
              nom={nomComplet(fiche)}
              modifiable={rh && !anonymisee}
              onChange={(maj) => setFiche(maj)}
            />
          </div>
          {/* Rappel de l'annonce (demande du 2026-09-19) : sur une fiche ouverte
              depuis le suivi, la recherche ou les flèches, on ne sait plus à
              quel poste ce candidat répond. L'intitulé était noyé dans la ligne
              grise : il est maintenant annoncé et lisible. */}
          <p className="meta rappel-annonce">
            <span className="pour">Candidature pour</span>
            <Link to={`/annonces/${fiche.annonceId}`} className="intitule-annonce">
              {fiche.annonceIntitule}
            </Link>
            <code>{fiche.annonceReference}</code>
            {/* Écarté : la mention passe à droite, en face de la note (2026-09-19). */}
            {fiche.statut !== 'ecarte' && <Statut valeur={fiche.statut} />}
          </p>
          {/* Règle 8 : la personne a repostulé sur la même annonce par le
              formulaire public. Sa candidature est restée la même. */}
          {fiche.premiereCandidatureLe && (
            <p className="repostule">
              Première candidature le {jourLisible(fiche.premiereCandidatureLe)} · a de nouveau
              postulé le {jourLisible(fiche.recueLe)}
              {fiche.soumissions > 2 ? ` (${fiche.soumissions} fois)` : ''}
            </p>
          )}
        </div>
        {/* Le bloc s'affiche aussi SANS numéro pour un profil RH : c'est
            précisément la fiche où l'on a besoin d'en ajouter un. Un lecteur
            sans droit aux téléphones, lui, ne voit toujours rien. */}
        {(fiche.telephone || (rh && !anonymisee)) && (
          <div className="telephone-fiche">
            <span className="numero">
              {[fiche.telephone, ...fiche.autresTelephones]
                .filter((numero): numero is string => numero !== null)
                .map((numero, i) => (
                <span key={numero}>
                  {i > 0 && <span className="separateur" aria-hidden="true"> · </span>}
                  {(i === 0 && fiche.telephone !== null) || anonymisee || ecarte ? (
                    telephoneLisible(numero)
                  ) : (
                    <a
                      href={`tel:${numero}`}
                      aria-label={`Appeler ${nomComplet(fiche)} au ${telephoneLisible(numero)}`}
                      onClick={() => rh && void ouvrirContact('telephonique')}
                    >
                      {telephoneLisible(numero)}
                    </a>
                  )}
                  {/* Seuls les numéros ajoutés à la main se reprennent : un
                      numéro lu dans un CV reviendrait à la lecture suivante. */}
                  {rh && !anonymisee && fiche.telephonesAjoutes.includes(numero) && (
                    <RetirerTelephone candidatureId={fiche.id} numero={numero} onChange={setFiche} />
                  )}
                </span>
              ))}
              {!fiche.telephone && fiche.autresTelephones.length === 0 && (
                <span className="sans-numero">Aucun numéro</span>
              )}
            </span>
            {rh && !anonymisee && <AjouterTelephone candidatureId={fiche.id} onChange={setFiche} />}
            {fiche.telephone && !anonymisee && !ecarte && (
              <a
                className="appeler"
                href={`tel:${fiche.telephone}`}
                aria-label={`Appeler ${nomComplet(fiche)}`}
                // Le numéro se compose ; pour un profil RH, le panneau d'appel attend le raccrochage.
                onClick={() => rh && void ouvrirContact('telephonique')}
              >
                <IconeTelephone /> Appeler
              </a>
            )}
            {/* WhatsApp : le lien s'ouvre dans le WhatsApp de l'utilisateur,
                où c'est LUI qui écrit et envoie. L'application n'écrit à
                personne (§ Interdits). Même droit d'accès que le téléphone. */}
            {fiche.whatsapp && !anonymisee && !ecarte && (
              <a
                className="lien-whatsapp-fiche"
                href={lienWhatsapp(fiche.whatsapp) ?? undefined}
                target="_blank"
                rel="noreferrer noopener"
                aria-label={`Ouvrir WhatsApp avec ${nomComplet(fiche)} au ${whatsappLisible(fiche.whatsapp)}`}
              >
                WhatsApp {whatsappLisible(fiche.whatsapp)}
              </a>
            )}
            {/* « Jamais appelé » devant une fiche sans le moindre numéro ne
                dit rien : le bloc n'est là que pour en ajouter un. */}
            {(fiche.telephone || fiche.autresTelephones.length > 0) && (
              <DernierAppel le={fiche.dernierAppelLe} joignable={fiche.dernierAppelJoignable} siAucun="Jamais appelé" />
            )}
            {/* Un numéro se partage — une famille, un cybercafé. On le SIGNALE,
                on ne fusionne jamais : mélanger deux candidats serait pire. */}
            {fiche.memeWhatsapp.length > 0 && (
              <p className="meme-whatsapp">
                Ce numéro WhatsApp est aussi celui de{' '}
                {fiche.memeWhatsapp.map((autre, i) => (
                  <span key={autre.personneId}>
                    {i > 0 && ', '}
                    <Link to={`/candidatures/${autre.candidatureId}`}>{autre.nom}</Link>
                  </span>
                ))}
              </p>
            )}
          </div>
        )}
        <div className="coin-droit">
          {fiche.statut === 'ecarte' && <Statut valeur={fiche.statut} />}
          <Note valeur={fiche.noteEntretien} date={fiche.noteLe} />
          {/* Passage au dossier voisin sans repasser par la liste (2026-09-19). */}
          <FlechesParcours voisins={parcours} />
        </div>
      </header>

      <Signaux candidature={fiche} />
      {fiche.anonymiseeLe && (
        <p className="signal signal-info">
          Profil anonymisé le {new Date(fiche.anonymiseeLe).toLocaleDateString('fr-FR')} : coordonnées, documents,
          observations et motifs effacés. Restent le statut, la pastille et la note, pour les statistiques.
        </p>
      )}
      {rh && fiche.purgePrevueLe && (
        <div className="ligne-conservation">
          <p className="signal-alerte">
            Anonymisation prévue le {jourLisible(fiche.purgePrevueLe)}, {quandPurge(fiche.purgePrevueLe, jourDeTunis())}
          </p>
          <Prolonger personneId={fiche.personneId} onProlongee={() => recharger()} />
        </div>
      )}
      {fiche.motifSortie && <p className="signal">Motif : {fiche.motifSortie}</p>}
      {/* Sur un dossier écarté, la pastille 0 vient de l'écart lui-même : le motif
          l'explique déjà, inutile de parler de pose à la main. */}
      {fiche.potentielManuelLe && fiche.statut !== 'ecarte' && (
        <p className="mention">
          Pastille posée à la main le {new Date(fiche.potentielManuelLe).toLocaleDateString('fr-FR')}, hors
          appel : le prochain appel noté la remplacera.
        </p>
      )}
      {fiche.origine && (
        <p className="origine-vivier">
          {rappelOrigine(fiche.origine)} —{' '}
          <Link to={`/candidatures/${fiche.origine.candidatureId}`}>voir l’entretien d’origine</Link>
        </p>
      )}
      {fiche.employe && (
        <p className="origine-vivier">
          {fiche.employe.statut === 'actif'
            ? `En poste depuis le ${fiche.employe.dateEntree.split('-').reverse().join('/')}`
            : `Sorti le ${fiche.employe.dateSortie?.split('-').reverse().join('/')}`}{' '}
          — <Link to={`/employes/${fiche.employe.id}`}>fiche employé</Link>
        </p>
      )}
      {rh && !anonymisee && <Embaucher fiche={fiche} />}

      {rh && !anonymisee && (
        <NoterPotentiel
          candidatureId={fiche.id}
          potentiel={fiche.potentiel}
          etiquette={fiche.etiquette}
          nom={nomComplet(fiche)}
          onChange={setFiche}
        />
      )}
      {rh && !anonymisee && (
        <div className="actions-fiche">
          <button type="button" className="bouton-second" onClick={() => void ouvrirContact('telephonique')}>
            Noter un appel
          </button>
          <button type="button" className="bouton-second" onClick={() => void ouvrirContact('presentiel')}>
            Entretien présentiel
          </button>
          <ChangerStatut candidatureId={fiche.id} statut={fiche.statut} onChange={setFiche} />
          <Ecarter candidatureId={fiche.id} statut={fiche.statut} onChange={setFiche} />
          {/* Droit accordé compte par compte par le super admin (migration 024). */}
          {peutDeplacer && fiche.statut !== 'embauche' && (
            <DeplacerCandidature
              candidatureId={fiche.id}
              annonceId={fiche.annonceId}
              annonceIntitule={fiche.annonceIntitule ?? 'son annonce'}
              nomCandidat={nomComplet(fiche)}
              onChange={setFiche}
            />
          )}
          <NePlusContacter
            personneId={fiche.personneId}
            depuis={fiche.nePlusContacterLe}
            motif={fiche.nePlusContacterMotif}
            onChange={() => recharger()}
          />
        </div>
      )}
      {rh && !anonymisee && (
        <div className="actions-fiche">
          <EditerCharte candidatureId={fiche.id} onEditee={(charte) => recharger(charte.documentId)} />
          {aCharte && (
            <Link className="bouton-second lien-bouton" to={`/candidatures/${fiche.id}/charte/scan`}>
              Scanner la charte signée
            </Link>
          )}
          <EditerRapport candidatureId={fiche.id} onEdite={(rapport) => recharger(rapport.documentId)} />
          {aRapport && (
            <Link className="bouton-second lien-bouton" to={`/candidatures/${fiche.id}/rapport/scan`}>
              Scanner le rapport signé
            </Link>
          )}
        </div>
      )}
      {erreurAction && <Erreur message={erreurAction} />}

      {/* Ce que le candidat a écrit lui-même, avant les pièces du dossier :
          c'est la seule source où il parle de lui avec ses mots. */}
      <FormulaireRecu fiche={fiche} onOuvrirCv={setOuvert} />

      {/* Le CV d'abord : c'est ce qu'on lit avant d'appeler. */}
      <section className="bloc">
        <h3>CV et documents</h3>
        {!aUnCv && (
          <p className="mention">
            {tunisieTravail
              ? rh && !anonymisee
                ? 'Aucun CV reçu de Tunisie Travail : téléchargez-le depuis votre espace recruteur, puis déposez-le ici.'
                : 'Aucun CV reçu : Tunisie Travail ne le transmet pas par e-mail.'
              : fiche.documents.length === 0
                ? 'Aucune pièce jointe reçue.'
                : 'Aucun CV reçu.'}
          </p>
        )}
        {rh && !anonymisee && (
          <div className="actions-fiche">
            {fiche.lienPlateforme && <LienVersPlateforme lien={fiche.lienPlateforme} />}
            <DeposerCv
              candidatureId={fiche.id}
              aUnCv={aUnCv}
              onDepose={(documentId) => recharger(documentId ?? undefined)}
            />
          </div>
        )}
        {rh && !anonymisee && !aUnCv && fiche.lienPlateforme && (
          <p className="mention">{MENTIONS_LIEN_PLATEFORME[fiche.lienPlateforme.type]}</p>
        )}
        {rh && !anonymisee && <CvDeposeFiche fiche={fiche} version={versionCv} onChange={() => recharger()} />}
        {ouvert && <LecteurDocument document={ouvert} />}
        <ul className="documents">
          {fiche.documents.map((document) => (
            <li key={document.id}>
              <button type="button" onClick={() => setOuvert(document)}>
                {document.nomFichier}
              </button>
              <span className="etiquettes">
                <span>{LIBELLES_TYPE_DOCUMENT[document.type] ?? document.type}</span>
                {document.ocr && <span title="Texte reconstitué par OCR">OCR</span>}
                {!document.lisible && <span className="alerte">texte non extrait</span>}
              </span>
              <button
                type="button"
                className="lien"
                onClick={() => void telechargerDocument(document.id, document.nomFichier)}
              >
                Télécharger
              </button>
              {ancienWord(document) && <p className="mention avertissement-doc">{AVERTISSEMENT_DOC}.</p>}
            </li>
          ))}
        </ul>
        {fiche.liensExternes.length > 0 && (
          <div className="liens">
            <p className="mention">
              Liens de partage signalés dans le message, non téléchargés :
            </p>
            <ul>
              {fiche.liensExternes.map((lien) => (
                <li key={lien}>
                  <a href={lien} target="_blank" rel="noreferrer noopener">{lien}</a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section className="bloc">
        <h3>Synthèse automatique <BadgeAdequation valeur={fiche.adequation} /></h3>
        {fiche.synthese ? (
          <div className="synthese-detail">
            {fiche.synthese.experienceAnnees !== null && (
              <p>Expérience annoncée : {fiche.synthese.experienceAnnees} ans.</p>
            )}
            {fiche.synthese.pointsForts.length > 0 && (
              <p><strong>Points forts :</strong> {fiche.synthese.pointsForts.join(' · ')}</p>
            )}
            {fiche.synthese.vigilance.length > 0 && (
              <p><strong>Vigilance :</strong> {fiche.synthese.vigilance.join(' · ')}</p>
            )}
            <p className="mention">
              Aide au tri, sans valeur de décision : la note reste humaine.
            </p>
          </div>
        ) : (
          <p className="mention">Extraction en attente.</p>
        )}
      </section>

      {/* La feuille qu'on emporte pour aller voir le candidat (2026-09-22) :
          elle se remplit ici et s'imprime au logo, filigranée. */}
      {rh && (
        <FicheRecapitulative fiche={fiche} modifiable={!anonymisee} onChange={setFiche} />
      )}

      <section className="bloc">
        <h3>Confrontation aux critères de l’annonce</h3>
        {fiche.synthese?.criteres.length ? (
          <ul className="criteres">
            {fiche.synthese.criteres.map((critere) => (
              <li key={critere.critereId} className={COULEUR_VERDICT[critere.verdict]}>
                <span className="libelle">
                  {critere.libelle}
                  {critere.type === 'eliminatoire' && <em> (éliminatoire)</em>}
                </span>
                <span className="verdict">{critere.verdict}</span>
                {critere.extrait && <q>{critere.extrait}</q>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mention">Aucun critère déclaré sur cette annonce.</p>
        )}
      </section>

      {rh && !anonymisee && (
        <section className="bloc">
          <h3>Justificatifs</h3>
          <Justificatifs candidatureId={fiche.id} onChange={() => recharger()} />
        </section>
      )}

      <section className="bloc">
        <h3>Historique des contacts</h3>
        {contacts ? (
          <HistoriqueContacts contacts={contacts} utilisateurId={utilisateur?.id} envois={envois} />
        ) : (
          <Chargement quoi="des contacts" />
        )}
      </section>

      {fiche.autresCandidatures.length > 0 && (
        <section className="bloc">
          <h3>Autres candidatures de cette personne</h3>
          <ul className="autres">
            {fiche.autresCandidatures.map((autre) => (
              <li key={autre.id}>
                <Link to={`/candidatures/${autre.id}`}>{autre.annonceIntitule}</Link>
                <code>{autre.annonceReference}</code>
                <Statut valeur={autre.statut} />
                {autre.noteEntretien !== null && <span>note {autre.noteEntretien}</span>}
                <span>{new Date(autre.recueLe).toLocaleDateString('fr-FR')}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {superAdmin && !fiche.employe && (
        <SupprimerPersonne
          personneId={fiche.personneId}
          nbCandidatures={fiche.nbCandidaturesPersonne}
          onSupprimee={() => navigate(`/annonces/${fiche.annonceId}`, { replace: true })}
        />
      )}
    </article>
  );
}
